import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import { Button, Input, Sheet, SheetContent, SheetDescription, SheetTitle } from '@org/ui';
import { cpfDigits, formatCpf, isValidCpf } from '../../lib/access';
import { formatPrice } from './helpers';
import { P, btnGhost, btnRed, dmSerif, eyebrowStyle, fineStyle, oswald } from './theme';

// -- Paid checkout: a bottom sheet with two short steps — the order summary
//    and the payer details ASAAS needs (CPF, optional phone) — then the
//    browser goes to the ASAAS hosted payment page (Pix / boleto / card).
//    The base product is a monthly subscription; a journey product is a
//    one-off charge. The webhook unlocks access when the payment confirms. --

export type CheckoutProduct = {
  productId: string;
  name: string;
  priceCents: number;
  currency: string;
  /** 'monthly' = subscription (base access); anything else = one-off. */
  billing: 'monthly' | 'one_time';
  coverImageUrl?: string | null;
};

type Step = 'summary' | 'payer';
const STEPS: Step[] = ['summary', 'payer'];

function StepTitle({ children }: { children: string }) {
  return (
    <h2 style={{ fontFamily: dmSerif, fontWeight: 400, fontSize: 24, lineHeight: 1.1, margin: '4px 0 12px', color: P.darkText }}>
      {children}
    </h2>
  );
}

function Row({ label, value, big = false }: { label: string; value: string; big?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '8px 0', borderTop: `1px dashed ${P.lightBorder}`, fontSize: 14, color: P.darkText }}>
      <span>{label}</span>
      <b style={{ fontFamily: oswald, fontWeight: 600, letterSpacing: '0.06em', fontSize: big ? 20 : 14, textAlign: 'right' }}>{value}</b>
    </div>
  );
}

function Actions({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginTop: 14 }}>{children}</div>;
}

export function CheckoutSheet({
  open,
  onOpenChange,
  product,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: CheckoutProduct;
}) {
  const { t, i18n } = useTranslation('p2e');
  const [step, setStep] = useState<Step>('summary');
  const [cpf, setCpf] = useState('');
  const [phone, setPhone] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setStep('summary');
      setTouched(false);
    }
  }, [open]);

  const checkout = trpc.billing.createCheckout.useMutation({
    onSuccess: ({ checkoutUrl }) => {
      window.location.assign(checkoutUrl);
    },
  });

  const monthly = product.billing === 'monthly';
  const priceLabel =
    product.priceCents === 0 ? t('products.free') : formatPrice(product.priceCents, product.currency, i18n.language ?? 'pt');
  const priceWithCycle = monthly ? t('products.checkout.perMonth', { price: priceLabel }) : priceLabel;
  const cpfOk = isValidCpf(cpf);
  const current = STEPS.indexOf(step);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="products-sheet p-0 border-0 bg-transparent shadow-none" style={{ maxHeight: '92vh', overflowY: 'auto' }}>
        <SheetTitle className="sr-only">{t('products.checkout.title')}</SheetTitle>
        <SheetDescription className="sr-only">{product.name}</SheetDescription>
        <div
          style={{
            background: P.white,
            border: `1px solid ${P.lightBorder}`,
            borderRadius: '14px 14px 0 0',
            boxShadow: '0 -6px 30px rgba(45,26,31,.12)',
            padding: '14px 18px calc(22px + env(safe-area-inset-bottom, 0px))',
          }}
        >
          <div aria-hidden style={{ width: 44, height: 5, borderRadius: 3, background: P.lightBorder, margin: '0 auto 12px' }} />
          <div aria-hidden style={{ display: 'flex', gap: 6, margin: '0 0 16px' }}>
            {STEPS.map((s, i) => (
              <i key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= current ? P.maxRed : P.lightBorder }} />
            ))}
          </div>

          {step === 'summary' && (
            <div>
              <div style={eyebrowStyle}>{t('products.checkout.step', { n: 1, total: 2, name: t('products.checkout.summaryName') })}</div>
              <StepTitle>{product.name}</StepTitle>
              <Row label={t('products.checkout.billing')} value={monthly ? t('products.checkout.billingMonthly') : t('products.checkout.billingOnce')} />
              <Row label={t('products.checkout.payment')} value={t('products.checkout.paymentMethods')} />
              <Row label={t('products.sheet.total')} value={priceWithCycle} big />
              <p style={{ ...fineStyle, margin: '10px 0 0' }}>
                {monthly ? t('products.checkout.noteMonthly') : t('products.checkout.noteOnce')}
              </p>
              <Actions>
                <Button type="button" variant="outline" className="products-btn" style={btnGhost} onClick={() => onOpenChange(false)}>
                  {t('products.sheet.back')}
                </Button>
                <Button type="button" variant="red" className="products-btn" style={btnRed} onClick={() => setStep('payer')}>
                  {t('products.sheet.next')}
                </Button>
              </Actions>
            </div>
          )}

          {step === 'payer' && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setTouched(true);
                if (!cpfOk || checkout.isPending) return;
                checkout.mutate({ productId: product.productId, cpfCnpj: cpfDigits(cpf), phone: phone.trim() || undefined });
              }}
            >
              <div style={eyebrowStyle}>{t('products.checkout.step', { n: 2, total: 2, name: t('products.checkout.payerName') })}</div>
              <StepTitle>{t('products.checkout.payerTitle')}</StepTitle>
              <p style={{ margin: '0 0 12px', fontSize: 15, lineHeight: 1.55, color: P.darkText }}>{t('products.checkout.payerBody')}</p>

              <label htmlFor="checkout-cpf" style={{ ...fineStyle, display: 'block', marginBottom: 6, color: P.midText }}>
                {t('products.checkout.cpfLabel')}
              </label>
              <Input
                id="checkout-cpf"
                inputMode="numeric"
                autoComplete="off"
                placeholder="000.000.000-00"
                value={cpf}
                onChange={(e) => setCpf(formatCpf(e.target.value))}
                onBlur={() => setTouched(true)}
                aria-invalid={touched && !cpfOk}
                style={{ borderColor: touched && !cpfOk ? P.maxRed : P.lightBorder, background: P.cream, fontSize: 16 }}
              />
              {touched && !cpfOk && (
                <p role="alert" style={{ ...fineStyle, color: P.maxRed, margin: '6px 0 0' }}>
                  {t('products.checkout.cpfInvalid')}
                </p>
              )}

              <label htmlFor="checkout-phone" style={{ ...fineStyle, display: 'block', margin: '12px 0 6px', color: P.midText }}>
                {t('products.checkout.phoneLabel')}
              </label>
              <Input
                id="checkout-phone"
                inputMode="tel"
                autoComplete="tel"
                placeholder="(11) 99999-9999"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                style={{ borderColor: P.lightBorder, background: P.cream, fontSize: 16 }}
              />

              {checkout.isError && (
                <p role="alert" style={{ ...fineStyle, color: P.maxRed, margin: '10px 0 0' }}>
                  {t('products.checkout.error')}
                </p>
              )}

              <Actions>
                <Button type="button" variant="outline" className="products-btn" style={btnGhost} onClick={() => setStep('summary')} disabled={checkout.isPending}>
                  {t('products.sheet.back')}
                </Button>
                <Button type="submit" variant="red" className="products-btn" style={btnRed} isLoading={checkout.isPending || checkout.isSuccess} loadingText={t('products.checkout.redirecting')}>
                  {t('products.checkout.pay')}
                </Button>
              </Actions>
              <p style={{ ...fineStyle, textAlign: 'center', margin: '12px 0 0' }}>{t('products.checkout.secureNote')}</p>
            </form>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
